import React, { useEffect, useState } from 'react';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/glyph/chevron-right';

import { LevelInfo, LevelsInfo, Translations as TranslationsType } from '../../interfaces';
import {
    IS_IN_TAB_CONTEXT,
    IS_LEVEL_NAME_SEARCH,
    IS_LEVEL_NAME_TRANSLATION,
    IS_LEVEL_NAME_TRANSLATION_SEARCH,
    LEVEL_DESCRIPTION_KEY,
    LEVEL_DESCRIPTION_TRANSLATION_KEY,
    LEVEL_NAME_KEY,
    LEVEL_NAME_TRANSLATION_KEY,
    LEVELS_INFO_TEXTS,
} from '../../utils/constants';

import { OptionTranslationValue } from './options-adder/interfaces';
import InputWithHeaderAndDescription from './InputWithHeaderAndDescription';
import { LevelsInfoWrapper, TranslateText, TranslateTextWrapper } from './styles';
import Translations from './Translations';

export type LevelInfoValue = string | OptionTranslationValue;

type Props = {
    currentLevel: number;
    firstOptionKey: string;
    levelsInfo: LevelsInfo;
    locale: string;
    nestingLevelChanged: boolean;
    onTextChange: (keyToUpdate: typeof LEVEL_NAME_KEY | typeof LEVEL_DESCRIPTION_KEY, value: string) => void;
    onTranslationChange: (
        keyToUpdate: typeof LEVEL_NAME_TRANSLATION_KEY | typeof LEVEL_DESCRIPTION_TRANSLATION_KEY,
        value: OptionTranslationValue
    ) => void;
    parentOptionKey: string;
};

export const getMatchingLevelInfo = (
    levelsInfo: LevelsInfo,
    currentLevel: number,
    firstOptionKey: string
): LevelInfo | undefined => {
    const possibleInfos = levelsInfo[`_${currentLevel}`];

    return possibleInfos.find(infoItem => infoItem.firstOptionKey === firstOptionKey);
};

export const getMatchingLevelInfoByParent = (
    levelsInfo: LevelsInfo,
    currentLevel: number,
    parentOptionKey: string
): LevelInfo | undefined => {
    const possibleInfos = levelsInfo[`_${currentLevel}`];

    return possibleInfos.find(infoItem => infoItem.parentOptionKey && infoItem.parentOptionKey === parentOptionKey);
};

const getLevelInfoValueTranslation = (
    levelsInfo: LevelsInfo,
    currentLevel: number,
    firstOptionKey: string,
    parentOptionKey: string,
    isLevelNameTranslationSearch = false
): Partial<TranslationsType> => {
    const matchingLevelInfo =
        firstOptionKey ?
            getMatchingLevelInfo(levelsInfo, currentLevel, firstOptionKey)
        :   getMatchingLevelInfoByParent(levelsInfo, currentLevel, parentOptionKey);
    if (matchingLevelInfo && matchingLevelInfo.levelInfo) {
        const { levelNameTranslation = {}, levelDescriptionTranslation = {} } = matchingLevelInfo.levelInfo;

        return isLevelNameTranslationSearch ? levelNameTranslation : levelDescriptionTranslation;
    }

    return {};
};

const getLevelInfoValue = (
    levelsInfo: LevelsInfo,
    currentLevel: number,
    firstOptionKey: string,
    parentOptionKey: string,
    isLevelNameSearch = false
): string => {
    const matchingLevelInfo =
        firstOptionKey ?
            getMatchingLevelInfo(levelsInfo, currentLevel, firstOptionKey)
        :   getMatchingLevelInfoByParent(levelsInfo, currentLevel, parentOptionKey);

    if (matchingLevelInfo && matchingLevelInfo.levelInfo) {
        const { levelName = '', levelDescription = '' } = matchingLevelInfo.levelInfo;

        return isLevelNameSearch ? levelName : levelDescription;
    }

    return '';
};

const LevelsInformation: React.FC<Props> = ({
    levelsInfo,
    onTextChange,
    onTranslationChange,
    currentLevel,
    firstOptionKey,
    parentOptionKey,
    locale,
    nestingLevelChanged,
}) => {
    const [isLevelNameTranslateExpanded, setIsLevelNameTranslateExpanded] = useState(false);
    const [isLevelNameDescriptionExpanded, setIsLevelDescriptionTranslateExpanded] = useState(false);

    useEffect(() => {
        if (nestingLevelChanged) {
            setIsLevelNameTranslateExpanded(false);
            setIsLevelDescriptionTranslateExpanded(false);
        }
    }, [nestingLevelChanged]);

    return (
        <LevelsInfoWrapper>
            <InputWithHeaderAndDescription
                value={getLevelInfoValue(levelsInfo, currentLevel, firstOptionKey, parentOptionKey, IS_LEVEL_NAME_SEARCH)}
                header={LEVELS_INFO_TEXTS.levelNameHeader}
                description={LEVELS_INFO_TEXTS.levelNameDescription}
                onChange={(value: string) => onTextChange(LEVEL_NAME_KEY, value)}
            />
            <TranslateTextWrapper onClick={() => setIsLevelNameTranslateExpanded(!isLevelNameTranslateExpanded)}>
                <TranslateText>Translate</TranslateText>
                {isLevelNameTranslateExpanded ?
                    <ChevronDownIcon label='translate' />
                :   <ChevronRightIcon label='translate' />}
            </TranslateTextWrapper>
            {isLevelNameTranslateExpanded && (
                <Translations
                    translation={getLevelInfoValueTranslation(
                        levelsInfo,
                        currentLevel,
                        firstOptionKey,
                        parentOptionKey,
                        IS_LEVEL_NAME_TRANSLATION_SEARCH
                    )}
                    originalValue={getLevelInfoValue(
                        levelsInfo,
                        currentLevel,
                        firstOptionKey,
                        parentOptionKey,
                        IS_LEVEL_NAME_SEARCH
                    )}
                    optionIndex={0}
                    locale={locale}
                    onChange={(
                        keyToUpdate: typeof LEVEL_NAME_TRANSLATION_KEY | typeof LEVEL_DESCRIPTION_TRANSLATION_KEY,
                        translationValue: OptionTranslationValue
                    ) => onTranslationChange(keyToUpdate, translationValue)}
                    isInTabContext={!IS_IN_TAB_CONTEXT}
                />
            )}
            <InputWithHeaderAndDescription
                value={getLevelInfoValue(levelsInfo, currentLevel, firstOptionKey, parentOptionKey, !IS_LEVEL_NAME_SEARCH)}
                header={LEVELS_INFO_TEXTS.levelDescriptionHeader}
                description={LEVELS_INFO_TEXTS.levelDescriptionText}
                onChange={(value: string) => onTextChange(LEVEL_DESCRIPTION_KEY, value)}
            />
            <TranslateTextWrapper onClick={() => setIsLevelDescriptionTranslateExpanded(!isLevelNameDescriptionExpanded)}>
                <TranslateText>Translate</TranslateText>
                {isLevelNameDescriptionExpanded ?
                    <ChevronDownIcon label='translate' />
                :   <ChevronRightIcon label='translate' />}
            </TranslateTextWrapper>
            {isLevelNameDescriptionExpanded && (
                <Translations
                    translation={getLevelInfoValueTranslation(
                        levelsInfo,
                        currentLevel,
                        firstOptionKey,
                        parentOptionKey,
                        !IS_LEVEL_NAME_TRANSLATION_SEARCH
                    )}
                    originalValue={getLevelInfoValue(
                        levelsInfo,
                        currentLevel,
                        firstOptionKey,
                        parentOptionKey,
                        !IS_LEVEL_NAME_SEARCH
                    )}
                    optionIndex={1}
                    locale={locale}
                    onChange={(
                        keyToUpdate: typeof LEVEL_NAME_TRANSLATION_KEY | typeof LEVEL_DESCRIPTION_TRANSLATION_KEY,
                        translationValue: OptionTranslationValue
                    ) => onTranslationChange(keyToUpdate, translationValue)}
                    isLevelNameTranslation={!IS_LEVEL_NAME_TRANSLATION}
                    isInTabContext={!IS_IN_TAB_CONTEXT}
                />
            )}
        </LevelsInfoWrapper>
    );
};

export default LevelsInformation;
